import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
  }
}

export class NetworkRequestError extends Error {
  constructor(cause: unknown) {
    super("Unable to reach AlwaysAI.", { cause });
    this.name = "NetworkRequestError";
  }
}

async function fetchApi(
  input: RequestInfo | URL,
  init: RequestInit
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    throw new NetworkRequestError(error);
  }
}

/*
 * A 401 that survives the single refresh retry means the Clerk session is gone
 * for good. main.tsx registers the sign-out handler while the app is mounted;
 * without one the request path only rejects as it always has. The guard fires
 * at most once per page load because every in-flight request fails the same
 * way at once and Clerk's sign-out already navigates away.
 */
let unauthenticatedHandler: (() => void) | null = null;
let unauthenticatedHandlerFired = false;

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  unauthenticatedHandler = handler;
}

export function resetUnauthenticatedHandlerForTests(): void {
  unauthenticatedHandler = null;
  unauthenticatedHandlerFired = false;
}

function notifyUnauthenticated(): void {
  if (unauthenticatedHandlerFired) return;

  if (unauthenticatedHandler === null) return;

  unauthenticatedHandlerFired = true;

  try {
    unauthenticatedHandler();
  } catch {
    // The handler owns its own failure; the request path still rejects with the 401.
  }
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;

  if (input instanceof URL) return input.href;

  return input.url;
}

function recordUnauthorizedRetry(input: RequestInfo | URL): void {
  const inputUrl = requestUrl(input);
  let path: string;

  try {
    path = new URL(inputUrl, "http://localhost").pathname;
  } catch {
    path = inputUrl.split(/[?#]/, 1)[0] || "/";
  }

  Sentry.addBreadcrumb({
    category: "auth",
    message: "retrying after 401",
    level: "warning",
    data: { path: telemetrySafeApiPath(path) },
  });
}

export async function fetchWithAuthRetry(
  getToken: ClerkGetToken,
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  /*
   * Auth rides Clerk's same-origin __session cookie, which is available to
   * both fetch and EventSource (unlike an Authorization header); a single
   * transport keeps stream replay working in production as well as locally.
   * The token value is deliberately unused: forcing a refresh updates that
   * cookie, the credential sent by the retried same-origin fetch.
   * Retrying POSTs is safe only because server/auth/middleware.ts emits 401
   * before handlers run. Reusing init.body assumes a re-sendable string body.
   */
  const requestInit = { ...init, credentials: "same-origin" as const };
  let response = await fetchApi(input, requestInit);

  if (response.status === 401) {
    recordUnauthorizedRetry(input);
    await getToken({ skipCache: true }).catch(() => null);
    response = await fetchApi(input, requestInit);

    if (response.status === 401) notifyUnauthenticated();
  }

  return response;
}

export function telemetrySafeApiPath(path: string): string {
  return path.replace(
    /^\/api\/appointments\/[^/]+(?=\/|$)/,
    "/api/appointments/:appointmentId"
  );
}

export function isNetworkRequestError(error: unknown): boolean {
  return error instanceof NetworkRequestError;
}

export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 404;
}

export function isAccessDeniedError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 403;
}

export function isUnauthenticatedError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 401;
}

export async function authenticatedApi<T>(
  getToken: ClerkGetToken,
  path: string,
  init?: RequestInit
): Promise<T> {
  const response = await fetchWithAuthRetry(getToken, path, init);

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => undefined);

    throw new ApiRequestError(
      `Request failed with ${response.status}`,
      response.status,
      body
    );
  }

  return response.json() as Promise<T>;
}
